import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

import {
  fetchAgents,
  postSession,
} from "../../services/Interviews/sessionService";
import SmashLoader from "../Loader/SmashLoader";

const ChooseAgent = ({ isOpen, setIsOpen, interview, user }) => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (signal) => {
    try {
      setLoading(true);
      const result = await fetchAgents({ signal });

      const agent_data = result.data.results;
      console.log("agent_data", agent_data);

      setAgents(agent_data);
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const handlePostSession = () => {
    postSession({
      interview: interview.uuid,
      person: user.uuid,
      agent: selectedAgent.uuid,
    }).then((res) => {
      navigate(`/interviews/session/${res.uuid}`);
    });
  };

  if (loading) return <SmashLoader />;

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogBackdrop
        transition
        className="fixed z-50 inset-0 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in w-screen h-screen bg-gradient-to-br from-gray-900/80 via-blue-900/20 to-purple-900/40 backdrop-blur-md"
      />
      <DialogPanel
        transition
        className="fixed left-1/2 top-1/2 z-50 flex w-full max-w-screen-md -translate-x-1/2 -translate-y-1/2 px-4 transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="w-full flex-auto overflow-hidden rounded-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl text-sm shadow-2xl ring-1 ring-black/5 dark:ring-white/10 border border-gray-200/20 dark:border-gray-700/30 max-h-[90vh] overflow-y-auto">
          <div className="p-6 md:p-8">
            {/* Header Section */}
            <div className="flex flex-col gap-2 mb-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="p-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                  <span className="text-xl">🙌</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Choose Your Interviewer
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-lg mx-auto">
                Select the AI interviewer for your interview session.
              </p>
              {selectedAgent && (
                <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200/50 dark:border-green-700/30">
                  <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircleIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Selected: {selectedAgent.name}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Agents Grid */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {agents.map((item, index) => (
                <div
                  key={index}
                  className="group relative transform transition-all duration-300 hover:scale-105"
                >
                  <button
                    className={`relative w-full flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-500/30 ${
                      selectedAgent?.uuid === item.uuid
                        ? "border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 shadow-lg shadow-blue-500/25"
                        : "border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100/80 dark:hover:bg-gray-700/80"
                    }`}
                    onClick={() => setSelectedAgent(item)}
                  >
                    {/* Selection Indicator */}
                    {selectedAgent?.uuid === item.uuid && (
                      <div className="absolute -top-2 -right-2 z-10">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-900 shadow-lg">
                          <CheckCircleIcon className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}

                    {/* Agent Image */}
                    <div className="relative w-full aspect-square rounded-lg overflow-hidden mb-3 shadow-md">
                      <img
                        src={item.preview_image_url}
                        alt={item.name}
                        className="w-60 aspect-square object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>

                    {/* Agent Name */}
                    <div className="text-center">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                        {item.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        AI Interviewer
                      </p>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </button>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-end items-center pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
              <button
                className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300/30 order-2 sm:order-1"
                onClick={() => {
                  setSelectedAgent(null);
                  setIsOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                disabled={!selectedAgent}
                className={`w-full sm:w-auto px-8 py-3 rounded-xl font-semibold transition-all duration-300 focus:outline-none focus:ring-4 order-1 sm:order-2 ${
                  selectedAgent
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl focus:ring-blue-500/30 transform hover:scale-105"
                    : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                }`}
                onClick={() => {
                  if (selectedAgent) {
                    handlePostSession();
                  }
                }}
              >
                <span className="flex items-center gap-2">
                  🚀 Start Interview
                </span>
              </button>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
};

export default ChooseAgent;
